import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import moment from 'moment';
import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
	AIChatAssistantMessage,
	AssistantChatInput,
	UniversalAssistantLayout,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../';
import { useEventLogging } from '../../../../../models/Logging';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { invalidateAllContentCalendarSuggestions, useAdminAccountQuery } from '../../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { invalidateAllAIConversationFilterQueries } from '../../../../../queries/AIConversation/useAIConversationFilterQuery';
import { useApproveSuggestedContentMutation } from '../../../../../queries/ContentCalendarSuggestion/useApproveSuggestedContentMutation';
import { useApproveSuggestedContentPreviewMutation } from '../../../../../queries/ContentCalendarSuggestion/useApproveSuggestedContentPreviewMutation';
import { Button } from '../../../../components-v2/Button';
import { AIConversationMessage } from '../../../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../../components-v2/ai/ChatConversation';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { WarningIcon, WarningIconType } from '../../../../components/svgs/icons/WarningIcon';
import { bs } from '../../../../styles/styles';
import { s } from '../styles';
import { styleSheet } from './styles';

const CONTENT_CALENDAR_ASSISTANT_NAME = 'ContentCalendarAgent';

interface ContentCalendarAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	onUseAIEmail?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const ContentCalendarAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function ContentCalendarAssistantChat(props: ContentCalendarAssistantChatProps) {
	return (
		<ContentCalendarAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<ContentCalendarAssistantChartInstance {...props} />
		</ContentCalendarAssistantChatErrorBoundary>
	);
}

const ContentCalendarGenerationPreviewConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: false,
		isCta: true,
		title: 'Override and Continue',
	},
	{
		isCancel: true,
		isCta: false,
		title: 'Cancel',
	},
];

export function ContentCalendarAssistantChartInstance({
	onClose,
	renderConversationStarters,
	impersonationContext,
}: ContentCalendarAssistantChatProps) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('ContentCalendarAssistantChat');
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});

	const {
		mutate: createUniversalAssistantConversation,
		data: createdConversation,
		isPending: isCreatingConversation,
	} = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});

	const [showApproveContentCalendarModal, setShowApproveContentCalendarModal] = useState(false);
	const [generateContentToolCall, setGenerateContentToolCall] = useState<Api.ContentCalendarResourceToolCall | null>(
		null
	);

	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	const { mutate: approveSuggestedContent, isPending: isApprovingSuggestedContent } =
		useApproveSuggestedContentMutation({
			onSuccess: () => {
				invalidateAllContentCalendarSuggestions();
				setGenerateContentToolCall(null);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Approve-ContentCalendar-Error`, error);
			},
		});

	const { mutate: approveSuggestedContentPreview, isPending: isApprovingSuggestedContentPreview } =
		useApproveSuggestedContentPreviewMutation({
			onSuccess: data => {
				if (data?.values?.length > 0) {
					setShowApproveContentCalendarModal(true);
					return;
				}

				setShowApproveContentCalendarModal(false);

				if (!generateContentToolCall) {
					return;
				}

				handleApproveContentCalendar(generateContentToolCall);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Approve-ContentCalendar-Preview-Error`, error);
			},
		});

	const handleApproveContentCalendarPreview = (toolCall: Api.ContentCalendarResourceToolCall) => {
		setGenerateContentToolCall(toolCall);

		approveSuggestedContentPreview({
			calendarId: toolCall.calendarId,
			impersonationContext,
		});
	};

	const handleApproveContentCalendar = (toolCall: Api.ContentCalendarResourceToolCall) => {
		approveSuggestedContent({
			calendarId: toolCall.calendarId,
			contentCalendarSuggestion: toolCall,
			impersonationContext,
		});
	};

	const onRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (canceled) {
			setShowApproveContentCalendarModal(false);
			setGenerateContentToolCall(null);
			return;
		}

		setShowApproveContentCalendarModal(false);

		if (!generateContentToolCall) {
			return;
		}

		handleApproveContentCalendar(generateContentToolCall);
	};

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(createdConversation?.id),
		id: createdConversation?.id,
		staleTime: Infinity,
		impersonationContext,
	});

	useEffect(() => {
		handleNewChatClick();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
	});

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};

	const handleDurationButtonClick = (duration: '1MONTH' | '3MONTHS') => {
		const startDate = moment();
		const endDate = duration === '1MONTH' ? moment().add(1, 'month') : moment().add(3, 'months');

		if (!conversationId) {
			return;
		}

		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				action: {
					_type: 'ContentCalendarGenerationAction',
					startDate: startDate.format('YYYY-MM-DD'),
					endDate: endDate.format('YYYY-MM-DD'),
				},
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	if (isCreatingConversation || !createdConversation?.id) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	const conversationId = createdConversation.id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						<div className={css(styleSheet.assistantDefaultMessageContent)}>
							<span>
								Hi {userSession.user.firstName}! How far out would you like the content planned for{' '}
								{`${accountQuery.data?.companyName ?? 'this account'}`}?
							</span>

							<div className={css(styleSheet.durationButtonsContainer)}>
								<Button variant='secondary' onClick={() => handleDurationButtonClick('1MONTH')}>
									1-Month
								</Button>
								<Button variant='secondary' onClick={() => handleDurationButtonClick('3MONTHS')}>
									3-Months
								</Button>
							</div>
						</div>
					</AIChatAssistantMessage>

					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onErrorTryAgain={handleTryAgain}
								onApproveContentCalendar={handleApproveContentCalendarPreview}
								conversationId={conversationId}
								onApproveContentCalendarLoading={isApprovingSuggestedContent || isApprovingSuggestedContentPreview}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>

			<ConfirmationDialog
				icon={<WarningIcon type={WarningIconType.Blue} />}
				modalProps={{
					isOpen: showApproveContentCalendarModal,
					onRequestClose,
				}}
				options={ContentCalendarGenerationPreviewConfirmationOptions}
				title={
					<div>
						<div>Warning</div>

						<p className={css(styleSheet.contentCalendarGenerationPreviewConfirmationText)}>
							You have already generated content on this <br /> calendar for the selected date range. If you <br />{' '}
							continue that content will be overwritten.
						</p>
					</div>
				}
			/>
		</UniversalAssistantLayout>
	);
}
